import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Grid, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Paper, Chip,
} from '@mui/material';
import AddIcon          from '@mui/icons-material/Add';
import UploadIcon       from '@mui/icons-material/Upload';
import UndoIcon         from '@mui/icons-material/Undo';
import RedoIcon         from '@mui/icons-material/Redo';
import DeleteSweepIcon  from '@mui/icons-material/DeleteSweep';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { transactionsAPI, accountsAPI, rulesAPI } from '../services/api';
import TransactionTable from '../components/TransactionTable';
import Navbar           from '../components/Navbar';

const defaultForm = {
  account_id: '', date: '', amount: '', description: '', category: '',
  transaction_type: 'expense', is_reimbursable: false, notes: '',
};

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const params = useParams();
  const highlightedTxId = params.txId ? parseInt(params.txId) : null;
  const [accounts, setAccounts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [form, setForm]                 = useState(defaultForm);
  const [editingId, setEditingId]       = useState(null);
  const [search, setSearch]             = useState('');
  const [filterType, setFilterType]     = useState('all');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFrom, setFilterFrom]   = useState('');
  const [filterTo, setFilterTo]       = useState('');
  const [page]                          = useState(1);
  const [importAccountId, setImportAccountId] = useState('');
  const [importing, setImporting]             = useState(false);
  const [showRuleManager, setShowRuleManager] = useState(false);
  const [newRulePattern, setNewRulePattern] = useState('');
  const [newRuleCategory, setNewRuleCategory] = useState('Income');

  // Undo/redo stacks — each entry: { label, ids, snapshot }
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Cross-account dupe warning dialog
  const [dupeDialog, setDupeDialog] = useState({
    open: false, count: 0, samples: [], pendingFile: null, pendingAccountId: null,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 500 };
      if (search) params.search = search;
      if (filterType !== 'all') params.transaction_type = filterType;
      if (filterCategory) params.category = filterCategory;
      if (filterFrom) params.from_date = filterFrom;
      if (filterTo) params.to_date = filterTo;
      const [txRes, acctRes] = await Promise.all([
        transactionsAPI.list(params),
        accountsAPI.list(),
      ]);
      setTransactions(txRes.data);
      setAccounts(acctRes.data);
      setImportAccountId((prev) =>
        prev || (acctRes.data.length > 0 ? String(acctRes.data[0].id) : '')
      );
    } catch {
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterType, filterCategory, filterFrom, filterTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle highlighted transaction from URL params
  useEffect(() => {
    if (highlightedTxId && transactions.length > 0) {
      const tx = transactions.find(t => t.id === highlightedTxId);
      if (tx) {
        setHighlightedTxId(highlightedTxId);
      }
    }
  }, [highlightedTxId, transactions]);

  // ── Universal Recategorization ────────────────────────
  const handleRecategorize = async (txId, newCategory) => {
    try {
      await transactionsAPI.recategorizeUniversal({ transaction_id: txId, category: newCategory });
      loadData();
    } catch {
      setError('Failed to recategorize transaction');
    }
  };

  // ── Add / Edit / Delete ───────────────────────────────
  const handleOpenAdd = () => {
    setForm({ ...defaultForm, account_id: accounts[0]?.id || '' });
    setEditingId(null);
    setDialogOpen(true);
  };
  const handleEdit = (tx) => {
    setForm({
      account_id: tx.account_id, date: tx.date?.split('T')[0] || '',
      amount: Math.abs(tx.amount), description: tx.description,
      category: tx.category || '', transaction_type: tx.transaction_type,
      is_reimbursable: tx.is_reimbursable, notes: tx.notes || '',
    });
    setEditingId(tx.id);
    setDialogOpen(true);
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try { await transactionsAPI.delete(id); loadData(); }
    catch { setError('Failed to delete'); }
  };
  const handleSave = async () => {
    try {
      const data = { ...form, amount: parseFloat(form.amount),
                     date: new Date(form.date).toISOString() };
      if (editingId) await transactionsAPI.update(editingId, data);
      else            await transactionsAPI.create(data);
      setDialogOpen(false);
      loadData();
    } catch { setError('Failed to save transaction'); }
  };

  // ── CSV import ────────────────────────────────────────
  const _doImport = async (file, accountId, force) => {
    setImporting(true); setError(''); setSuccess('');
    try {
      const res = await transactionsAPI.importCSV(parseInt(accountId, 10), file, force);
      const { imported, skipped, duplicates_skipped, ai_categorized,
              bank_detected, imported_ids, imported_snapshot } = res.data;

      if (imported_ids?.length > 0) {
        setUndoStack((prev) => [
          ...prev.slice(-9),
          { label: `Import ${imported} txns (${bank_detected || 'csv'})`,
            ids: imported_ids, snapshot: imported_snapshot },
        ]);
        setRedoStack([]);
      }

      const parts = ['✓ Imported ' + imported + ' transactions'];
      if (bank_detected)       parts[0] += ' from ' + bank_detected;
      if (skipped > 0)         parts.push(skipped + ' skipped');
      if (duplicates_skipped > 0) parts.push(duplicates_skipped + ' duplicates skipped');
      if (ai_categorized > 0)  parts.push(ai_categorized + ' AI-categorized');
      setSuccess(parts.join(' · '));
      setDupeDialog({ open: false, count: 0, samples: [],
                      pendingFile: null, pendingAccountId: null });
      loadData();
    } catch (err) {
      if (err.response?.status === 409) {
        const d = err.response.data;
        setDupeDialog({
          open: true, count: d.count || 0, samples: d.samples || [],
          pendingFile: file, pendingAccountId: accountId,
        });
      } else {
        setError('Import failed: ' + (err.response?.data?.detail || err.message));
      }
    } finally { setImporting(false); }
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!importAccountId) {
      setError('Select an account from "Import to Account" first.'); return;
    }
    _doImport(file, importAccountId, false);
  };

  const handleForceImport = () => {
    const { pendingFile, pendingAccountId } = dupeDialog;
    setDupeDialog((p) => ({ ...p, open: false }));
    _doImport(pendingFile, pendingAccountId, true);
  };

  // ── Undo / Redo ───────────────────────────────────────
  const handleUndo = async () => {
    if (!undoStack.length) return;
    const last = undoStack[undoStack.length - 1];
    try {
      await transactionsAPI.batchDelete(last.ids);
      setUndoStack((p) => p.slice(0, -1));
      setRedoStack((p) => [...p.slice(-9), last]);
      setSuccess('↩ Undone — removed ' + last.ids.length + ' transactions');
      loadData();
    } catch { setError('Undo failed'); }
  };

  const handleRedo = async () => {
    if (!redoStack.length) return;
    const last = redoStack[redoStack.length - 1];
    try {
      const res = await transactionsAPI.batchRestore(last.snapshot);
      const newIds = res.data.ids || [];
      setRedoStack((p) => p.slice(0, -1));
      setUndoStack((p) => [...p.slice(-9), { ...last, ids: newIds }]);
      setSuccess('↪ Redone — restored ' + newIds.length + ' transactions');
      loadData();
    } catch { setError('Redo failed'); }
  };

  // ── Delete All ────────────────────────────────────────
  const handleDeleteAll = async () => {
    if (!window.confirm(
      'Permanently delete ALL transactions?\nThis cannot be undone.\nClick OK to confirm.'
    )) return;
    try {
      await transactionsAPI.deleteAll();
      setUndoStack([]); setRedoStack([]);
      setSuccess('✓ All transactions deleted.');
      loadData();
    } catch { setError('Failed to delete all transactions'); }
  };

  // ── Render ────────────────────────────────────────────
  return (
    <>
      <Navbar />
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>

        {/* Header row */}
        <Box display="flex" justifyContent="space-between" alignItems="center"
             mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h4" fontWeight={700}>Transactions</Typography>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>

            {/* Undo */}
            <Tooltip title={undoStack.length
              ? 'Undo: ' + undoStack[undoStack.length - 1].label
              : 'Nothing to undo'}>
              <span>
                <Button variant="outlined" size="small" startIcon={<UndoIcon />}
                  onClick={handleUndo} disabled={!undoStack.length}>
                  Undo
                </Button>
              </span>
            </Tooltip>

            {/* Redo */}
            <Tooltip title={redoStack.length
              ? 'Redo: ' + redoStack[redoStack.length - 1].label
              : 'Nothing to redo'}>
              <span>
                <Button variant="outlined" size="small" startIcon={<RedoIcon />}
                  onClick={handleRedo} disabled={!redoStack.length}>
                  Redo
                </Button>
              </span>
            </Tooltip>

            <Box sx={{ width: '1px', height: 28,
                       bgcolor: 'divider', mx: 0.5, alignSelf: 'center' }} />

            {/* Import account picker */}
            {accounts.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Import to Account</InputLabel>
                <Select value={importAccountId} label="Import to Account"
                  onChange={(e) => setImportAccountId(e.target.value)}>
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button variant="outlined" component="label"
              startIcon={importing
                ? <CircularProgress size={16} color="inherit" />
                : <UploadIcon />}
              disabled={importing || accounts.length === 0}>
              {importing ? 'Importing…' : 'Import CSV'}
              <input type="file" accept=".csv" hidden onChange={handleImportCSV} />
            </Button>

            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              Add Transaction
            </Button>

            <Tooltip title="Permanently delete ALL transactions for this account">
              <Button variant="outlined" color="error" size="small"
                startIcon={<DeleteSweepIcon />} onClick={handleDeleteAll}>
                Delete All
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        {/* Alerts */}
        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Filters */}
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search transactions…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filterType} label="Type"
              onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="expense">Expense</MenuItem>
              <MenuItem value="transfer">Transfer</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select value={filterCategory} label="Category"
              onChange={(e) => setFilterCategory(e.target.value)}>
              <MenuItem value="">All Categories</MenuItem>
              {['Income','Transfer','Credit Card Payment','Savings','Family Support',
                'Charity/Donation','Food & Dining','Groceries','Transport','Subscriptions',
                'Utilities','Healthcare','Shopping','Housing','Entertainment','Travel',
                'Fees','Personal Care','Insurance','Uncategorized'].map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="From" type="date"
            value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
          <TextField size="small" label="To" type="date"
            value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
          {(filterCategory || filterFrom || filterTo) && (
            <Button size="small" variant="outlined" onClick={() => {
              setFilterCategory(''); setFilterFrom(''); setFilterTo('');
            }}>Clear Filters</Button>
          )}
        </Stack>

        {/* Table */}
                {/* Rule Manager */}
        <Box mb={2}>
          <Button 
            variant="outlined" 
            onClick={() => setShowRuleManager(!showRuleManager)}
            sx={{ mb: 1 }}
          >
            {showRuleManager ? 'Hide' : 'Show'} Rule Manager
          </Button>
          
          {showRuleManager && (
            <Card elevation={2} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Category Rules & Recategorization
                </Typography>
                
                {/* Add Rule Form */}
                <Box display="flex" gap={1} alignItems="center" mb={2}>
                  <TextField 
                    size="small" 
                    placeholder="Pattern (e.g., 'amazon')" 
                    value={newRulePattern}
                    onChange={(e) => setNewRulePattern(e.target.value)}
                    sx={{ minWidth: 150 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Category</InputLabel>
                    <Select value={newRuleCategory} label="Category" onChange={(e) => setNewRuleCategory(e.target.value)}>
                      {['Income','Food & Dining','Groceries','Transport',
                        'Shopping','Utilities','Healthcare','Entertainment',
                        'Travel','Fees','Personal Care','Charity/Donation'].map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={() => {
                      if (newRulePattern && newRuleCategory) {
                        rulesAPI.add({ pattern: newRulePattern, category: newRuleCategory }).then(() => {
                          setNewRulePattern('');
                          setNewRuleCategory('Income');
                          loadData();
                          setShowRuleManager(false);
                        });
                      }
                    }}
                  >
                    Add Rule
                  </Button>
                </Box>

                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => transactionsAPI.recategorize().then(() => loadData())}
                >
                  Re-categorize All Uncategorized
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>

{loading
          ? <Box textAlign="center" py={4}><CircularProgress /></Box>
          : <TransactionTable 
              transactions={transactions}
              onEdit={handleEdit} 
              onDelete={handleDelete}
              onRecategorize={handleRecategorize} />
        }

        {/* Add / Edit dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingId ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account</InputLabel>
                  <Select value={form.account_id} label="Account" required
                    onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
                    {accounts.map((a) => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Date" type="date" fullWidth size="small"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Amount" type="number" fullWidth size="small"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={form.transaction_type} label="Type"
                    onChange={(e) => setForm({ ...form, transaction_type: e.target.value })}>
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="income">Income</MenuItem>
                    <MenuItem value="transfer">Transfer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Description" fullWidth size="small"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} required />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Category" fullWidth size="small"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Notes" fullWidth size="small"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Cross-account duplicate warning */}
        <Dialog open={dupeDialog.open}
          onClose={() => setDupeDialog((p) => ({ ...p, open: false }))}
          maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" />
            Similar Transactions Found in Another Account
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{dupeDialog.count}</strong> transaction
              {dupeDialog.count !== 1 ? 's' : ''} in this file appear to already exist
              in a different account. Importing may create duplicates.
            </Alert>
            {dupeDialog.samples.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dupeDialog.samples.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency', currency: 'USD'
                          }).format(row.amount)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden',
                                         textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.description}
                        </TableCell>
                      </TableRow>
                    ))}
                    {dupeDialog.count > 10 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Chip size="small"
                            label={'+ ' + (dupeDialog.count - 10) + ' more'} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              Exact duplicates within the <em>same</em> account are always skipped
              automatically — only cross-account matches require your decision.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDupeDialog((p) => ({ ...p, open: false }))}>
              Cancel
            </Button>
            <Button variant="contained" color="warning" onClick={handleForceImport}>
              Import Anyway
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </>
  );
}
